import { useState, useCallback, useMemo, useRef } from 'react';

import { Group, Person } from '@mui/icons-material';
import {
  Box,
  Typography,
  Stack,
  Chip,
  useTheme,
  AlertColor,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress
} from '@mui/material';
import {
  GridRenderCellParams,
  GridRowSelectionModel,
  GridValueFormatterParams,
  getGridSingleSelectOperators
} from '@mui/x-data-grid';

import Page from '@/components/Page';
import Alert from '@/components/alert';
import Avatar from '@/components/avatar/Avatar';
import Button from '@/components/button/Button';
import DashboardCard from '@/components/dashboardCard';
import DataTable from '@/components/dataTable/DataTable';
import NoRowsOverlay from '@/components/dataTable/customNoRowsOverlay/NoRowsOverlay';
import OnBoardingOverlay from '@/components/dataTable/customNoRowsOverlay/OnBoardingOverlay';
import dateRangeOperator from '@/components/dataTable/customOperators/dateRangeOperator';
import ExportCreators from '@/components/dataTable/customToolbar/ExportCreators';
import Header from '@/components/header/Header';
import InstagramIcon from '@/components/icons/InstagramIcon';
import TikTokIcon from '@/components/icons/TikTokIcon';
import YoutubeIcon from '@/components/icons/YoutubeIcon';
import Toolbar from '@/components/toolbar/Toolbar';
import { creatorsSteps } from '@/constants/driverSteps';
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE } from '@/constants/table';
import { useFetchCreatorGroups } from '@/hooks/queries/creatorGroups';
import { useFetchCreators } from '@/hooks/queries/creators';
import { useGetScraperStatus } from '@/hooks/queries/scraper';
import { useFetchWorkspaces } from '@/hooks/queries/workspaces';
import { useAppSelector } from '@/hooks/redux';
import { useDebounce } from '@/hooks/useDebounce';
import useDriver from '@/hooks/useDriver';
import useTableParams from '@/hooks/useTableParams';
import useWorkspaceNavigate from '@/hooks/useWorkspaceNavigate';
import { Creator } from '@/models/creator';
import { Scraper } from '@/models/scraper';
import { formatDate } from '@/utils/datesAndTime/datesAndTime';
import getRoute from '@/utils/getRoute/getRoute';
import nFormatter from '@/utils/nFormatter/nFormatter';
import Error from '@/views/misc/error';

import CreateCreator from '../create/CreateCreator';
import DeleteCreators from '../delete/DeleteCreators';
import ImportCreators from '../import/ImportCreators';
import AddCreatorsGroups from './_partials/AddCreatorsGroups';
import CreatorGroupsModal from './_partials/CreatorGroupsModal';
import MergeCreators from './_partials/MergeCreators';

const defaultParams = {
  page: DEFAULT_PAGE,
  size: DEFAULT_PAGE_SIZE,
  name: undefined,
  sort: undefined,
  order: undefined,
  platform: undefined,
  groups: undefined,
  creationDate: undefined,
  status: undefined
};

const getInitialFilterModelItems = (params: Record<string, any>) => {
  const { platform, groups, creationDate } = params;

  const items = [];

  if (platform !== undefined)
    items.push({
      field: 'platform',
      id: items.length,
      operator: 'is',
      value: platform
    });

  if (groups !== undefined) {
    items.push({
      field: 'groups',
      id: items.length,
      operator: 'isAnyOf',
      value: groups.split(',')
    });
  }
  if (creationDate !== undefined)
    items.push({
      field: 'createdAt',
      id: items.length,
      operator: 'between',
      value: creationDate.split(',').map((item: string) => new Date(item))
    });

  return items;
};

const CreatorsOnBoardingOverlay = () => (
  <OnBoardingOverlay
    icon={<Person color="action" sx={{ width: 40, height: 40 }} />}
    title="Start list of Creators"
    text="Add creators to analyse their content when creating evaluations."
  />
);

const getStatus = (
  accountStatus: Array<Creator.PlatformAccount['status']>
) => ({
  color:
    (
      [
        { status: 'missing', color: 'error.main' },
        { status: 'private', color: 'error.main' },
        { status: 'outdated', color: 'error.main' },
        { status: 'pending', color: 'warning.main' }
      ] as Array<{
        status: Creator.PlatformAccount['status'];
        color: string;
      }>
    ).find(({ status }) => accountStatus.includes(status))?.color ?? 'default'
});

const platformIcons: Record<string, React.ReactNode> = {
  instagram: <InstagramIcon />,
  tiktok: <TikTokIcon />,
  youtube: <YoutubeIcon />
};

const CreatorsList = () => {
  const theme = useTheme();
  const { params, handlers, updateParams, paramsInTheUrl } = useTableParams(
    getRoute.creators.LIST(),
    defaultParams
  );

  const [searchValue, setSearchValue] = useState<string>(params.name || '');
  const deferredCreatorsValue = useDebounce(searchValue, 500);
  const navigate = useWorkspaceNavigate();

  const [isOpenCreateCreatorDialog, setIsOpenCreateCreatorDialog] =
    useState<boolean>(false);
  const [isOpenImportListDialog, setIsOpenImportListDialog] =
    useState<boolean>(false);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState<boolean>(false);
  const [isGroupsDialog, setIsOpenGroupsDialog] = useState<boolean>(false);
  const [isMergeDialog, setIsOpenMergeDialog] = useState<boolean>(false);
  const [selectedCreators, setSelectedCreators] =
    useState<GridRowSelectionModel>([]);
  const [groupsModalRow, setGroupsModalRow] = useState<{
    name: string;
    groups: Creator.Groups;
  } | null>(null);

  const {
    data: creators,
    isFetching,
    error
  } = useFetchCreators({
    params: {
      ...params,
      name: deferredCreatorsValue ? String(deferredCreatorsValue) : undefined,
      groups: params.groups ? String(params.groups).split(',') : undefined
    }
  });

  const { data: groups, isLoading: isLoadingGroups } = useFetchCreatorGroups({
    params: {}
  });

  const { data: scraperStatus } = useGetScraperStatus();
  const user = useAppSelector((state) => state.user.currentUser);
  const { data: workspaces = [] } = useFetchWorkspaces({});
  const currentWorkspace = workspaces?.find(
    (ws) => ws.id === user?.workspaceId
  );
  const initialFilterModel = useRef({
    items: getInitialFilterModelItems(params)
  });
  const isLimited = Boolean(
    currentWorkspace?.maxAccounts &&
      currentWorkspace?.currentAccounts &&
      currentWorkspace.currentAccounts >= currentWorkspace.maxAccounts
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchValue(value);
    updateParams({
      name: value.replace(/^\s+/g, ''),
      page: DEFAULT_PAGE
    });
  };

  const getGroupsFlex = (count: number) => {
    if (count === 0) return 1;
    if (count > 4) return 3;
    return 2;
  };

  const groupsCount = groups?.results?.length ?? 0;

  const columnFlex = {
    name: 1.5,
    platform: 1,
    groups: getGroupsFlex(groupsCount),
    contentCount: 1,
    followerCount: 1
  };

  const groupFilterWidth = (groups?.results ?? []).reduce(
    (max, { name }) => Math.max(max, name.length * 10),
    0
  );

  const columns = useMemo(
    () => [
      {
        filterable: false,
        disableColumnMenu: true,
        field: 'name',
        sortable: false,
        headerName: 'Creator',
        minWidth: 200,
        flex: columnFlex.name,
        renderCell: (data: any) => {
          const { name, avatarUrl } = data.row;

          return (
            <Stack
              direction="row"
              alignItems="center"
              overflow="hidden"
              gap={1}
            >
              <Avatar
                src={avatarUrl || ''}
                name={name}
                width="40px"
                fontSize={16}
              />
              <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {name}
              </Typography>
            </Stack>
          );
        }
      },
      {
        field: 'platform',
        headerName: 'Platform',
        sortable: false,
        type: 'singleSelect',
        valueOptions: [
          { value: 'instagram', label: 'Instagram' },
          { value: 'tiktok', label: 'TikTok' },
          { value: 'youtube', label: 'YouTube' }
        ],
        filterOperators: getGridSingleSelectOperators().filter(
          (operator) => operator.value === 'is'
        ),
        minWidth: 150,
        flex: columnFlex.platform,
        renderCell: (data: any) => {
          const { platforms } = data.row;

          return (
            <Stack direction="row" gap={0.5}>
              {Object.entries(platforms).map(([platform, accountStatus]) => {
                const statuses = accountStatus as Array<
                  Creator.PlatformAccount['status']
                >;
                if (!statuses.length) return null;

                const { color } = getStatus(statuses);

                return (
                  <Box
                    key={platform}
                    display="flex"
                    alignItems="center"
                    sx={{ color }}
                  >
                    {platformIcons[platform]}
                  </Box>
                );
              })}
            </Stack>
          );
        }
      },
      {
        field: 'groups',
        headerName: 'Groups',
        sortable: false,
        type: 'singleSelect',
        valueOptions: (groups?.results ?? []).map(({ id, name }) => ({
          value: String(id),
          label: name
        })),
        filterOperators: getGridSingleSelectOperators()
          .filter((op) => op.value === 'isAnyOf')
          .map((op) => ({
            ...op,
            InputComponentProps: {
              ...op.InputComponentProps,
              sx: { width: groupFilterWidth }
            }
          })),
        minWidth: 400,
        flex: columnFlex.groups,
        renderCell: (data: any) => {
          const { groups, name } = data.row;

          if (isLoadingGroups) {
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 48
                }}
              >
                <CircularProgress size={20} color="inherit" />
              </div>
            );
          }

          const visibleGroups = groups.slice(0, 4);
          const hiddenCount = groups.length - visibleGroups.length;

          return (
            <Stack
              direction="row"
              gap={0.5}
              alignItems="center"
              flexWrap="wrap"
            >
              {visibleGroups.map(
                ({ id, title }: { id: number; title: string }) => (
                  <Chip
                    key={id}
                    label={title}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(getRoute.creatorGroups.DETAIL(id));
                    }}
                    sx={{
                      flexShrink: 0,
                      cursor: 'pointer',
                      transition:
                        'background-color 0.15s ease, color 0.15s ease',
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText'
                      }
                    }}
                  />
                )
              )}
              {hiddenCount > 0 && (
                <Chip
                  label={`+${hiddenCount}`}
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    setGroupsModalRow({ name, groups });
                  }}
                  sx={{ flexShrink: 0, cursor: 'pointer' }}
                />
              )}
            </Stack>
          );
        }
      },
      {
        field: 'createdAt',
        headerName: 'Creation Date',
        minWidth: 160,
        type: 'date',
        filterOperators: dateRangeOperator,
        valueFormatter: (
          params: GridValueFormatterParams<Creator.ListParams>
        ) => String(params.value),
        flex: 1,
        renderCell: (data: GridRenderCellParams<Creator.Item>) => {
          const { createdAt } = data.row;

          return (
            <Typography>
              {formatDate(new Date(createdAt), 'dd/MM/yyyy HH:mm')}
            </Typography>
          );
        }
      },
      {
        filterable: false,
        disableColumnMenu: true,
        field: 'contentCount',
        headerName: 'Posts Retrieved',
        minWidth: 100,
        flex: columnFlex.contentCount,
        renderCell: (data: any) => {
          const { contentCount } = data.row;

          return <Typography>{nFormatter(contentCount)}</Typography>;
        }
      },
      {
        filterable: false,
        disableColumnMenu: true,
        field: 'followerCount',
        headerName: 'Followers',
        minWidth: 100,
        flex: columnFlex.followerCount,
        renderCell: (data: any) => {
          const { followerCount } = data.row;

          return <Typography>{nFormatter(followerCount)}</Typography>;
        }
      }
    ],
    [creators]
  );

  const onFilterModelChange = useCallback(
    (filterParams: Record<string, any>) => {
      const newParams: Record<string, any> = {};
      const fieldToParamKey: Record<string, string> = {
        createdAt: 'creationDate'
      };

      filterParams.items.forEach((item: { field: string; value: any }) => {
        const { field, value } = item;

        if (field === 'platform') {
          newParams.platform = value as string;
        }
        if (field === 'groups') {
          newParams.groups = Array.isArray(value) ? value.join(',') : value;
        }
        if (field === 'createdAt' && value?.length > 0) {
          const formattedDates: string[] = value
            .map((date: Date) => formatDate(date, 'yyyy-MM-dd'))
            .filter(Boolean);

          newParams.creationDate = formattedDates.length
            ? formattedDates.join(',')
            : undefined;
        }
      });

      // If no filters are active, clear all parameters
      columns
        .filter((column) => column.field)
        .forEach((column) => {
          const paramKey = fieldToParamKey[column.field] ?? column.field;

          const filterExistsInTheUrl =
            Object.keys(paramsInTheUrl).includes(paramKey);
          const isFilterActive = filterParams.items.some(
            (item: { field: string }) => item.field === column.field
          );

          if (filterExistsInTheUrl && !isFilterActive) {
            newParams[paramKey] = undefined;
          }
        });

      updateParams({
        ...paramsInTheUrl,
        ...newParams,
        page: DEFAULT_PAGE
      });
    },
    [paramsInTheUrl, updateParams]
  );

  let emptyStateCreators = CreatorsOnBoardingOverlay;
  if (params.name !== undefined) emptyStateCreators = () => <NoRowsOverlay />;

  const { status } = params;

  const channelAlert = useCallback((): {
    severity: AlertColor | undefined;
    message: string;
  } => {
    if (!scraperStatus) {
      return { severity: undefined, message: '' };
    }

    const platforms =
      Object.values<Scraper.Status[Scraper.Platform]>(scraperStatus);

    const hasError = platforms.some(
      (platform) =>
        (platform.missingPrivateProfiles ?? 0) > 0 ||
        (platform.outdatedProfiles ?? 0) > 0
    );

    if (hasError) {
      return {
        severity: 'error',
        message: 'Some accounts are not fully functional.'
      };
    }

    const hasWarning = platforms.some(
      (platform) => (platform.pendingProfiles ?? 0) > 0
    );

    if (hasWarning) {
      return {
        severity: 'warning',
        message: 'Some accounts are still pending.'
      };
    }

    return { severity: undefined, message: '' };
  }, [scraperStatus]);

  const workspaceAlert = useCallback((): {
    severity: AlertColor | undefined;
    message: string;
    action?: string;
  } => {
    if (!currentWorkspace?.currentAccounts || !currentWorkspace?.maxAccounts) {
      return { severity: undefined, message: '' };
    }
    const { currentAccounts, maxAccounts } = currentWorkspace;
    const accountMessage = `Accounts: ${currentAccounts}/${maxAccounts}`;

    if (currentAccounts >= maxAccounts) {
      return {
        severity: 'error',
        message: accountMessage,
        action: 'Your workspace has reached the maximum number of accounts.'
      };
    }

    return {
      severity: 'info',
      message: accountMessage
    };
  }, [currentWorkspace]);

  const isAlertOpen = channelAlert().message !== '';
  const isWorkspaceAlert = workspaceAlert().message !== '';

  useDriver({
    isLoading: isFetching,
    steps: creatorsSteps
  });

  if (error) return <Error message="Failed to get creators" error={error} />;

  return (
    <Page
      header={
        <Header
          title="Creators"
          actions={
            <>
              <ToggleButtonGroup
                value="creators"
                size="small"
                sx={{
                  backgroundColor: theme.palette.background.paper
                }}
              >
                <ToggleButton
                  value="creators"
                  onClick={() => navigate('/creators')}
                >
                  <Person />
                </ToggleButton>
                <ToggleButton
                  id="creator-groups-bttn"
                  onClick={() => navigate('/creator-groups')}
                  value="creatorGroups"
                >
                  <Group />
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                id="create-creator"
                text="add creator"
                onClick={() => setIsOpenCreateCreatorDialog(true)}
                disabled={isLimited}
              />
              <Button
                id="creator-import-list"
                text="import list"
                onClick={() => setIsOpenImportListDialog(true)}
                disabled={isLimited}
              />
            </>
          }
        />
      }
      toolbar={
        <Toolbar
          hasSelection={selectedCreators}
          onClickDelete={() => setIsOpenDeleteDialog(true)}
          onClickGroups={() => setIsOpenGroupsDialog(true)}
          onClickMerge={() => setIsOpenMergeDialog(true)}
          onChangeSearch={handleSearchChange}
          showGroupsButton
          showMergeButton
          showDeleteButton
          showSearchInput
          searchValue={searchValue}
          sx={isAlertOpen ? { marginBottom: '0px' } : {}}
        />
      }
      title="Creators"
    >
      <Stack gap={2}>
        <Box>
          <DashboardCard
            id="creators-count"
            icon={<Person sx={{ color: 'white' }} />}
            title="Creators"
            value={creators?.totalItems || 0}
            description={
              <Stack gap={1}>
                {isWorkspaceAlert && (
                  <Alert
                    id="creators-limit-workspace"
                    sx={{ alignItems: 'center' }}
                    severity={workspaceAlert().severity}
                    content={workspaceAlert().message}
                    action={workspaceAlert().action}
                  />
                )}
                {isAlertOpen && (
                  <Alert
                    id="creators-filter"
                    severity={channelAlert().severity}
                    content={channelAlert().message}
                    action={
                      <Button
                        sx={{
                          backgroundColor: !status
                            ? 'transparent'
                            : theme.palette.action.selected,
                          border: status ? '1px solid transparent' : undefined
                        }}
                        text="Filter"
                        color="inherit"
                        variant="outlined"
                        onClick={() => {
                          if (!status) {
                            updateParams({
                              ...paramsInTheUrl,
                              status: 'missing,private,pending,outdated',
                              page: DEFAULT_PAGE
                            });
                            return;
                          }
                          updateParams({
                            ...paramsInTheUrl,
                            status: undefined
                          });
                        }}
                      />
                    }
                  />
                )}
              </Stack>
            }
          />
        </Box>
        <Box id="creators-table">
          <DataTable
            getRowId={(row) => row.id}
            columns={columns}
            rowHeight={80}
            rows={creators?.results || []}
            rowCount={creators?.totalItems}
            checkboxSelection
            keepNonExistentRowsSelected
            loading={isFetching}
            sortingMode="server"
            filterMode="server"
            paginationMode="server"
            onRowClick={(row) => navigate(getRoute.creators.DETAIL(row.id))}
            onChangePageSize={handlers.onChangePageSize}
            onChangePage={handlers.onChangePage}
            onSortModelChange={handlers.onSortModelChange}
            initialFilterModel={initialFilterModel.current}
            onFilterModelChange={onFilterModelChange}
            sort={params.sort}
            order={params.order}
            page={params.page}
            size={params.size}
            rowSelectionModel={selectedCreators}
            onRowSelectionModelChange={setSelectedCreators}
            noRowsOverlay={emptyStateCreators}
            toolbar={() => <ExportCreators creatorList={creators} />}
          />
        </Box>
        {isOpenCreateCreatorDialog && (
          <CreateCreator
            isOpen={isOpenCreateCreatorDialog}
            onClose={() => {
              setIsOpenCreateCreatorDialog(false);
            }}
          />
        )}
        {isOpenImportListDialog && (
          <ImportCreators
            isOpen={isOpenImportListDialog}
            onClose={() => {
              setIsOpenImportListDialog(false);
            }}
          />
        )}
        {isOpenDeleteDialog && (
          <DeleteCreators
            isOpen={isOpenDeleteDialog}
            onClose={() => {
              setIsOpenDeleteDialog(false);
            }}
            selected={selectedCreators}
            clearSelection={() => {
              setSelectedCreators([]);
            }}
            creatorsList={creators?.results || []}
          />
        )}
        {isGroupsDialog && (
          <AddCreatorsGroups
            isOpen={isGroupsDialog}
            onClose={() => {
              setIsOpenGroupsDialog(false);
            }}
            selected={selectedCreators}
            clearSelection={() => {
              setSelectedCreators([]);
            }}
            creatorsList={creators?.results || []}
          />
        )}
        {isMergeDialog && (
          <MergeCreators
            isOpen={isMergeDialog}
            onClose={() => {
              setIsOpenMergeDialog(false);
            }}
            selected={selectedCreators}
            clearSelection={() => {
              setSelectedCreators([]);
            }}
            creatorsList={creators?.results || []}
          />
        )}
        {groupsModalRow && (
          <CreatorGroupsModal
            isOpen={Boolean(groupsModalRow)}
            onClose={() => setGroupsModalRow(null)}
            creatorName={groupsModalRow.name}
            groups={groupsModalRow.groups}
          />
        )}
      </Stack>
    </Page>
  );
};

export default CreatorsList;
